import { Command } from 'commander';
import { randomUUID } from 'crypto';
import { execSync } from 'child_process';
import inquirer from 'inquirer';
import { writeCredentials } from '../credentials.js';
import { setActiveOrgSlug, setTelemetryConfig } from '../active-org.js';
import { ensureLoginConfig } from '../config.js';
import { V2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { isTelemetryEnabled, recordCliEvent } from '../services/telemetry.js';
import {
  isClaudeCodeInstalled,
  hasShownClaudeCodeNudge,
  markClaudeCodeNudgeShown,
  CLAUDE_CODE_PRESENT_MSG,
  CLAUDE_CODE_MISSING_MSG,
} from '../services/claude-code.js';

interface LoginOpts { noBrowser?: boolean; token?: string; telemetry?: boolean; }

/**
 * Test seams for the OAuth poll loop. Production callers omit these; tests
 * inject a no-op `sleep` (so the 2s poll interval doesn't slow the suite)
 * and a stub `fetchImpl` that returns an authenticated status immediately.
 */
interface LoginDeps {
  sleep?: (ms: number) => Promise<void>;
  fetchImpl?: typeof fetch;
}

interface OrgsListResp {
  orgs: Array<{ org_id: string; slug: string; name: string; role: string; member_count: number }>;
  primary_org_id: string | null;
}

interface MyInvitation {
  invitation_id: string;
  invited_by: { name: string };
  org: { slug: string; name: string };
  expires_at: string;
}

/**
 * v0.8 sign-in flow. OAuth via the API server's `/auth/login?code=…` page;
 * the server writes the resulting JWT into the session keyed by the same
 * code, and we poll `/auth/status?code=…` until it reports `authenticated`.
 *
 * v0.8 identity-model adjustments versus the v0.7 implementation:
 *
 *   - JWT is written ONLY to `~/.cloudgrid/credentials` (mode 0600). The
 *     legacy mirror into `~/.cloudgrid/config.yaml` is gone — the
 *     credentials helper is the single writer, and `createApiClient` reads
 *     credentials directly.
 *   - `known_orgs[]` is not cached locally. Backlog 041.2 dropped client-
 *     side org caching; org membership is resolved against the live
 *     `GET /api/v2/orgs` response on every command flow that needs it.
 *   - The active-org pick uses the user's `primary_org_id` when present,
 *     and falls back to a single-choice list (or interactive prompt) when
 *     the user belongs to 2+ orgs and no primary is set.
 */
export async function runLogin(opts: LoginOpts, deps: LoginDeps = {}): Promise<void> {
  // Decision 016: `--no-telemetry` writes the opt-out before the rest of
  // the flow runs, so even if the user aborts mid-login the preference
  // is persisted. Commander's `--no-foo` flag pattern sets `opts.foo = false`.
  if (opts.telemetry === false) {
    setTelemetryConfig(false);
  }
  // Self-heal config.yaml. A clean machine has no file; the startup
  // update-check writes a partial file (its own keys, no api_url). Either
  // way, `ensureLoginConfig` seeds the prod api_url default and persists,
  // preserving every other key — so login proceeds instead of crashing on
  // `cfg.api_url.replace(...)` (Tamir QA-01). A genuinely unreadable
  // (malformed-YAML) file throws a CloudGridError, which the command's
  // error handler turns into a clear stderr line and a non-zero exit.
  const cfg = ensureLoginConfig();
  const apiUrl = cfg.api_url.replace(/\/+$/, '');

  const jwt = opts.token ? opts.token : await oauthFlow(apiUrl, !!opts.noBrowser, deps);

  const payload = decodeJwtPayload(jwt);
  writeCredentials({
    jwt,
    issued_at: new Date().toISOString(),
    email: payload.email ?? '',
    user_id: payload.sub ?? payload.user_id ?? '',
  });

  const client = new V2Client({ apiUrl, jwt, orgSlug: null });
  const orgsResp = await client.get<OrgsListResp>('/api/v2/orgs');

  if (orgsResp.orgs.length === 0) {
    process.stderr.write(
      'You have no grid memberships yet. Accept an invitation or ask an admin to invite you.\n',
    );
    process.stdout.write(`✓ Signed in as ${payload.email}\n`);
    printTelemetryNotice();
    printClaudeCodeNudge();
    return;
  }

  const activeSlug = await pickActiveOrg(orgsResp);
  setActiveOrgSlug(activeSlug);
  const activeOrg = orgsResp.orgs.find((o) => o.slug === activeSlug)!;

  process.stdout.write(`✓ Signed in as ${payload.email}\n`);
  process.stdout.write(`✓ Active grid set to: ${activeOrg.name} (${activeOrg.slug})\n`);
  printTelemetryNotice();

  await surfacePendingInvitations(client);
  printClaudeCodeNudge();
}

/**
 * One-line notice on first sign-in and re-sign-in. Decision 016 §"Privacy
 * and anonymization" requires the default-on telemetry to be visible at
 * this discoverable moment. Notice is suppressed when the user has
 * explicitly opted out (env or config) — no point telling them about a
 * thing they've already disabled.
 */
function printTelemetryNotice(): void {
  if (!isTelemetryEnabled()) return;
  process.stdout.write(
    'Telemetry is on (errors + high-signal successes). Disable with --no-telemetry or CLOUDGRID_TELEMETRY=0.\n',
  );
}

/**
 * One-time Claude Code nudge on first sign-in. Shows whether `claude` is on
 * PATH and, if not, the install command. Writes a breadcrumb to
 * ~/.cloudgrid/ so the message doesn't repeat on subsequent logins.
 */
function printClaudeCodeNudge(): void {
  if (hasShownClaudeCodeNudge()) return;
  const msg = isClaudeCodeInstalled() ? CLAUDE_CODE_PRESENT_MSG : CLAUDE_CODE_MISSING_MSG;
  process.stdout.write(msg + '\n');
  markClaudeCodeNudgeShown();
}

async function pickActiveOrg(resp: OrgsListResp): Promise<string> {
  if (resp.orgs.length === 1) return resp.orgs[0].slug;
  if (resp.primary_org_id) {
    const primary = resp.orgs.find((o) => o.org_id === resp.primary_org_id);
    if (primary) return primary.slug;
  }
  const answer = await inquirer.prompt([
    {
      type: 'list',
      name: 'slug',
      message: `You belong to ${resp.orgs.length} grids. Pick the default for this machine:`,
      choices: resp.orgs.map((o) => ({ name: `${o.name} (${o.slug})`, value: o.slug })),
    },
  ]);
  return answer.slug as string;
}

async function surfacePendingInvitations(client: V2Client): Promise<void> {
  try {
    const pending = await client.get<{ invitations: MyInvitation[] }>(
      '/api/v2/users/me/invitations',
    );
    if (pending.invitations.length === 0) return;
    const n = pending.invitations.length;
    process.stdout.write(
      `\nYou have ${n} pending invitation${n === 1 ? '' : 's'}:\n`,
    );
    for (const inv of pending.invitations) {
      const expiresIn = daysUntil(inv.expires_at);
      process.stdout.write(
        `  - ${inv.org.name} (${inv.org.slug}) — invited by ${inv.invited_by.name} — expires in ${expiresIn} days\n`,
      );
    }
    process.stdout.write('Accept in the Console: https://console.cloudgrid.io/invitations\n');
  } catch {
    // Non-fatal — invitation surfacing is a courtesy, not a sign-in dependency.
  }
}

async function oauthFlow(
  apiUrl: string,
  noBrowser: boolean,
  deps: LoginDeps = {},
): Promise<string> {
  const sessionCode = randomUUID();
  const loginUrl = `${apiUrl}/auth/login?code=${sessionCode}`;
  if (noBrowser) {
    process.stdout.write(`Open this URL to sign in: ${loginUrl}\n`);
  } else {
    process.stdout.write('Opening your browser to sign in.\n');
    try {
      const platform = process.platform;
      if (platform === 'darwin') execSync(`open "${loginUrl}"`);
      else if (platform === 'linux') execSync(`xdg-open "${loginUrl}"`);
      else if (platform === 'win32') execSync(`start "${loginUrl}"`);
      else process.stdout.write(`Open this URL: ${loginUrl}\n`);
    } catch {
      process.stdout.write(`Open this URL: ${loginUrl}\n`);
    }
  }
  // The poll loop below is silent for up to five minutes. Without this line
  // the terminal looks frozen while the user is off in the browser; one plain
  // sentence tells them the CLI is waiting on them, not stuck. §23b: friendly,
  // brief, get-out-of-the-way.
  process.stdout.write('Waiting for you to finish in the browser...\n');
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const fetchImpl = deps.fetchImpl ?? fetch;
  for (let i = 0; i < 150; i++) {
    await sleep(2000);
    let data: { status?: string; jwt?: string };
    try {
      const res = await fetchImpl(`${apiUrl}/auth/status?code=${sessionCode}`);
      data = (await res.json()) as { status?: string; jwt?: string };
    } catch {
      // Network blips during polling are normal; keep going until the
      // outer 5-minute window expires below. The status checks live OUTSIDE
      // this catch on purpose: a deliberate `status: 'expired'` throw must
      // escape immediately instead of being swallowed as a blip, which would
      // make the CLI burn the full 5-minute poll budget before bailing.
      continue;
    }
    if (data.status === 'authenticated' && data.jwt) return data.jwt;
    if (data.status === 'expired') throw new Error('Authentication timed out.');
  }
  throw new Error('Authentication timed out. Run `cloudgrid login` to try again.');
}

function decodeJwtPayload(jwt: string): { email?: string; sub?: string; user_id?: string } {
  const parts = jwt.split('.');
  if (parts.length < 2) return {};
  try {
    return JSON.parse(Buffer.from(parts[1], 'base64url').toString());
  } catch {
    return {};
  }
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.floor(ms / 86400000));
}

export const loginCommand = new Command('login')
  .description('Sign in via Google OAuth')
  .option('--no-browser', 'Print the URL instead of opening the browser')
  .option('--token <jwt>', 'Use a pre-issued token instead of OAuth (for CI)')
  .option('--no-telemetry', 'Disable CLI telemetry (decision 016 — persisted to ~/.cloudgrid/config.yaml)')
  .action(async (opts: LoginOpts) => {
    const start = Date.now();
    try {
      await runLogin(opts);
      // Decision 016 — best-effort success event on the high-signal verb.
      try {
        await recordCliEvent({
          event_type: 'success',
          command: 'cloudgrid login',
          elapsed_ms: Date.now() - start,
        });
      } catch { /* swallow */ }
    }
    catch (err) { await handleV2Error(err); }
  });
